import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CalendarMonth as CalendarMonthIcon,
  Description as DescriptionIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import FiltroSucursalVentaSelect from '../../components/shared/FiltroSucursalVentaSelect';
import SearchableSelect from '../../components/SearchableSelect';
import ClienteInlineSearchField from '../../components/Tesoreria/ClienteInlineSearchField';
import ClienteSearch from '../../components/Tesoreria/ClienteSearch';
import { useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import ubicacionService, {
  etiquetaUbicacionSeleccion,
  type Ubicacion,
} from '../../services/ubicacionService';
import ventasInformesService, {
  type VentasResultadosComprobante,
} from '../../services/ventasInformesService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/apiError';

type QuickPreset =
  | 'hoy'
  | 'semana'
  | 'mes'
  | 'mesPasado'
  | 'tresMeses'
  | 'seisMeses'
  | 'anioActual'
  | 'custom';

const toInputDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const rangeForPreset = (key: Exclude<QuickPreset, 'custom'>): { from: string; to: string } => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const to = new Date(today);
  const from = new Date(today);

  switch (key) {
    case 'hoy':
      break;
    case 'semana': {
      const day = today.getDay();
      const diff = day === 0 ? 6 : day - 1;
      from.setDate(today.getDate() - diff);
      break;
    }
    case 'mes':
      from.setDate(1);
      break;
    case 'mesPasado':
      from.setMonth(today.getMonth() - 1, 1);
      to.setDate(0);
      break;
    case 'tresMeses':
      from.setMonth(today.getMonth() - 2, 1);
      break;
    case 'seisMeses':
      from.setMonth(today.getMonth() - 5, 1);
      break;
    case 'anioActual':
      from.setMonth(0, 1);
      break;
  }
  return { from: toInputDate(from), to: toInputDate(to) };
};

const formatComprobanteLabel = (c: VentasResultadosComprobante): string => {
  const tipo = (c.tipDescripcion || c.nroTipoFormulario || '').trim();
  const letra = (c.letra || '').trim();
  const suc = c.nroSuc != null ? String(c.nroSuc).padStart(4, '0') : '----';
  const nro = c.nroComprobante != null ? String(c.nroComprobante).padStart(8, '0') : '--------';
  const parts = [tipo, letra ? `${letra}` : '', `${suc}-${nro}`].filter(Boolean);
  return parts.join(' ');
};

const comprobanteKey = (c: VentasResultadosComprobante, idx: number): string =>
  c.nroIdformulario != null
    ? `id-${c.nroIdformulario}`
    : `k-${c.nroTipoFormulario}-${c.nroSuc}-${c.letra}-${c.nroComprobante}-${c.nroFormulario}-${idx}`;

const InformeVentasResultados: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const initialRange = useMemo(() => rangeForPreset('mes'), []);
  const [fechaDesde, setFechaDesde] = useState(initialRange.from);
  const [fechaHasta, setFechaHasta] = useState(initialRange.to);
  const [presetActivo, setPresetActivo] = useState<QuickPreset>('mes');
  const [nroCliente, setNroCliente] = useState<number | null>(null);
  const [nombreCliente, setNombreCliente] = useState('');
  const [openClienteSearch, setOpenClienteSearch] = useState(false);
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales, filtroListo } = useSucursalesVentaFiltro(null);
  const [ubicaciones, setUbicaciones] = useState<Ubicacion[]>([]);
  const [loadingUbicaciones, setLoadingUbicaciones] = useState(false);
  const [nroUbicacion, setNroUbicacion] = useState<number | null>(null);

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState<VentasResultadosComprobante[]>([]);
  const [totalImporte, setTotalImporte] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingUbicaciones(true);
        const list = await ubicacionService.getUbicaciones();
        if (!cancelled) setUbicaciones(list);
      } catch (e) {
        console.error(e);
        if (!cancelled) enqueueSnackbar('No se pudieron cargar ubicaciones.', { variant: 'warning' });
      } finally {
        if (!cancelled) setLoadingUbicaciones(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enqueueSnackbar]);

  const ubicacionesFiltradas = useMemo(() => {
    if (filtroNroSuc == null) return ubicaciones;
    return ubicaciones.filter((u) => Number(u.nroSuc) === Number(filtroNroSuc));
  }, [ubicaciones, filtroNroSuc]);

  useEffect(() => {
    if (nroUbicacion == null) return;
    const ok = ubicacionesFiltradas.some((u) => Number(u.nroUbicacion) === Number(nroUbicacion));
    if (!ok) setNroUbicacion(null);
  }, [ubicacionesFiltradas, nroUbicacion]);

  const applyQuickChip = (key: Exclude<QuickPreset, 'custom'>) => {
    const range = rangeForPreset(key);
    setFechaDesde(range.from);
    setFechaHasta(range.to);
    setPresetActivo(key);
  };

  const handleGenerar = useCallback(async () => {
    if (!fechaDesde || !fechaHasta) {
      enqueueSnackbar('Indicá fecha desde y hasta.', { variant: 'warning' });
      return;
    }
    if (fechaDesde > fechaHasta) {
      enqueueSnackbar('La fecha desde no puede ser mayor que la fecha hasta.', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      const res = await ventasInformesService.getRptVentasResultados({
        fechaDesde,
        fechaHasta,
        nroCliente: nroCliente ?? undefined,
        nroSuc: filtroNroSuc ?? undefined,
        nroUbicacion: nroUbicacion ?? undefined,
      });
      setRows(res.data);
      setTotalImporte(res.totalImporte ?? 0);
      setTruncated(Boolean(res.truncated));
      setExpanded({});
      setPage(0);
      if (res.truncated) {
        enqueueSnackbar(
          `Se truncó el resultado (${res.totalComprobantes} comprobantes / ${res.totalLineasMatched} líneas). Ajustá el rango o filtros.`,
          { variant: 'warning' }
        );
      } else {
        enqueueSnackbar(`${res.totalComprobantes} comprobantes encontrados.`, { variant: 'success' });
      }
    } catch (e) {
      console.error(e);
      enqueueSnackbar(getApiErrorMessage(e, 'No se pudo generar el informe.'), { variant: 'error' });
      setRows([]);
      setTotalImporte(0);
      setTruncated(false);
    } finally {
      setLoading(false);
    }
  }, [fechaDesde, fechaHasta, nroCliente, filtroNroSuc, nroUbicacion, enqueueSnackbar]);

  const pagedRows = useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage]
  );

  const toggleExpand = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const buildExportFlatRows = (): Record<string, string | number>[] =>
    rows.flatMap((c) => {
      const cab: Record<string, string | number> = {
        Fecha: c.fecha ? formatDate(c.fecha) : '',
        Comprobante: formatComprobanteLabel(c),
        Cliente: c.nroCliente != null ? `${c.nroCliente} - ${c.nombreCliente ?? ''}` : c.nombreCliente ?? '',
        Sucursal: c.nroSuc ?? '',
        Ubicacion: c.nroUbicacion ?? '',
        Vendedor: c.venNombreVendedor ?? '',
        'Forma de pago': c.formaDePago ?? '',
        'Importe total': c.importeTotal ?? 0,
      };
      if (!c.items?.length) {
        return [{ ...cab, Item: '', Código: '', Descripción: '', Cantidad: 0, 'P. unitario': 0, Subtotal: 0 }];
      }
      return c.items.map((it) => ({
        ...cab,
        Item: it.nroItem ?? '',
        Código: it.codigoArticulo ?? '',
        Descripción: it.descripcion ?? '',
        Cantidad: it.cantidad ?? 0,
        'P. unitario': it.precioUnitarioFinal ?? it.precioUnitario ?? 0,
        Subtotal: it.subtotalFinal ?? it.subtotal ?? 0,
      }));
    });

  const exportCsv = () => {
    if (rows.length === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'info' });
      return;
    }
    const flat = buildExportFlatRows();
    const headers = Object.keys(flat[0] ?? {});
    const lines = [
      headers.join(';'),
      ...flat.map((r) => headers.map((h) => String(r[h] ?? '')).join(';')),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `resultados-ventas_${fechaDesde}_${fechaHasta}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportExcel = () => {
    if (rows.length === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'info' });
      return;
    }
    const wb = XLSX.utils.book_new();
    const cabeceras = rows.map((c) => ({
      Fecha: c.fecha ? formatDate(c.fecha) : '',
      Comprobante: formatComprobanteLabel(c),
      Cliente: c.nroCliente != null ? `${c.nroCliente} - ${c.nombreCliente ?? ''}` : c.nombreCliente ?? '',
      Sucursal: c.nroSuc ?? '',
      Ubicacion: c.nroUbicacion ?? '',
      Vendedor: c.venNombreVendedor ?? '',
      'Forma de pago': c.formaDePago ?? '',
      'Importe sin IVA': c.importeSinIva ?? 0,
      'Importe total': c.importeTotal ?? 0,
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(cabeceras), 'Comprobantes');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(buildExportFlatRows()), 'Detalle');
    XLSX.writeFile(wb, `resultados-ventas_${fechaDesde}_${fechaHasta}.xlsx`);
  };

  const exportPdf = async () => {
    if (rows.length === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'info' });
      return;
    }
    try {
      setExporting(true);
      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const marginX = 36;
      let y = 40;

      doc.setFontSize(16);
      doc.text('Informe de resultados de ventas', marginX, y);
      y += 18;
      doc.setFontSize(10);
      doc.text(`Período: ${fechaDesde} a ${fechaHasta}`, marginX, y);
      y += 14;
      doc.text(`Generado: ${new Date().toLocaleString('es-AR')}  |  Total: ${formatCurrency(totalImporte)}`, marginX, y);
      y += 16;

      autoTable(doc, {
        startY: y,
        head: [['Fecha', 'Comprobante', 'Cliente', 'Suc.', 'Ubic.', 'Vendedor', 'Forma pago', 'Total']],
        body: rows.map((c) => [
          c.fecha ? formatDate(c.fecha) : '',
          formatComprobanteLabel(c),
          c.nroCliente != null ? `${c.nroCliente} - ${c.nombreCliente ?? ''}` : c.nombreCliente ?? '',
          c.nroSuc ?? '',
          c.nroUbicacion ?? '',
          c.venNombreVendedor ?? '',
          c.formaDePago ?? '',
          formatCurrency(c.importeTotal ?? 0),
        ]),
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [25, 118, 210], textColor: 255 },
        columnStyles: { 7: { halign: 'right' } },
      });

      const fileName = `resultados-ventas_${fechaDesde}_${fechaHasta}.pdf`;
      doc.save(fileName);
      enqueueSnackbar('PDF descargado correctamente.', { variant: 'success' });
    } catch (e) {
      console.error(e);
      enqueueSnackbar('No se pudo generar el PDF.', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ flex: 1, minWidth: 0 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/ventas/informes')}
                sx={{ textTransform: 'none' }}
              >
                Informes
              </Button>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  Resultados de ventas
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Seguimiento de ventas del período con detalle de artículos por comprobante.
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="contained"
              color="primary"
              startIcon={loading ? <CircularProgress size={22} color="inherit" /> : <DescriptionIcon />}
              onClick={() => void handleGenerar()}
              disabled={loading}
              sx={{
                flexShrink: 0,
                alignSelf: { xs: 'flex-end', sm: 'flex-start' },
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 3,
                py: 1,
              }}
            >
              Generar Informe
            </Button>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }} alignItems="center">
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontWeight: 600 }}>
              Período rápido
            </Typography>
            {(
              [
                { key: 'hoy' as const, label: 'Hoy' },
                { key: 'semana' as const, label: 'Semana actual' },
                { key: 'mes' as const, label: 'Mes actual' },
                { key: 'mesPasado' as const, label: 'Mes pasado' },
                { key: 'tresMeses' as const, label: 'Últ. 3 meses' },
                { key: 'seisMeses' as const, label: 'Últ. 6 meses' },
                { key: 'anioActual' as const, label: 'Año actual' },
              ] as const
            ).map(({ key, label }) => (
              <Chip
                key={key}
                icon={<CalendarMonthIcon sx={{ fontSize: 18, color: presetActivo === key ? 'inherit' : 'primary.main' }} />}
                label={label}
                size="small"
                onClick={() => applyQuickChip(key)}
                color={presetActivo === key ? 'primary' : 'default'}
                variant={presetActivo === key ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>

          <Card sx={{ mb: 3, borderRadius: 2, border: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Fecha desde"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={fechaDesde}
                    onChange={(e) => {
                      setFechaDesde(e.target.value);
                      setPresetActivo('custom');
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Fecha hasta"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={fechaHasta}
                    onChange={(e) => {
                      setFechaHasta(e.target.value);
                      setPresetActivo('custom');
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FiltroSucursalVentaSelect
                    value={filtroNroSuc}
                    onChange={setFiltroNroSuc}
                    sucursales={sucursales}
                    loading={loadingSucursales}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SearchableSelect
                    label="Ubicación"
                    allowEmpty
                    emptyOptionLabel="Todas"
                    loading={loadingUbicaciones}
                    size="small"
                    value={nroUbicacion}
                    onChange={(v) => setNroUbicacion(v ?? null)}
                    options={ubicacionesFiltradas.map((u) => ({
                      value: Number(u.nroUbicacion),
                      label: etiquetaUbicacionSeleccion(u),
                    }))}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <ClienteInlineSearchField
                        label="Cliente (opcional)"
                        placeholder="Número, nombre o CUIT — vacío = todos"
                        excluirInactivos={false}
                        displayValue={
                          nroCliente != null ? `${nroCliente} - ${nombreCliente}` : ''
                        }
                        onSelect={(c) => {
                          setNroCliente(c.nroCliente);
                          setNombreCliente(c.nombre ?? '');
                        }}
                        onOpenModal={() => setOpenClienteSearch(true)}
                      />
                    </Box>
                    {nroCliente != null && (
                      <Button
                        size="small"
                        onClick={() => {
                          setNroCliente(null);
                          setNombreCliente('');
                        }}
                        sx={{ mt: 1, textTransform: 'none', flexShrink: 0 }}
                      >
                        Limpiar
                      </Button>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }} alignItems="center">
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={exportCsv}
              disabled={rows.length === 0}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              CSV
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={exportExcel}
              disabled={rows.length === 0}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Excel
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={exporting ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
              onClick={() => void exportPdf()}
              disabled={rows.length === 0 || exporting}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              PDF
            </Button>
            {rows.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                {rows.length} comprobantes · Total {formatCurrency(totalImporte)}
                {truncated ? ' · (truncado)' : ''}
              </Typography>
            )}
          </Stack>

          <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell width={48} />
                  <TableCell>Fecha</TableCell>
                  <TableCell>Comprobante</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Suc.</TableCell>
                  <TableCell>Ubic.</TableCell>
                  <TableCell>Vendedor</TableCell>
                  <TableCell>Forma de pago</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : pagedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {rows.length === 0
                          ? 'Generá el informe para ver resultados.'
                          : 'Sin filas en esta página.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedRows.map((c, idx) => {
                    const key = comprobanteKey(c, page * rowsPerPage + idx);
                    const open = Boolean(expanded[key]);
                    return (
                      <React.Fragment key={key}>
                        <TableRow hover sx={{ '& > *': { borderBottom: open ? 'unset' : undefined } }}>
                          <TableCell>
                            <IconButton size="small" onClick={() => toggleExpand(key)} aria-label="Ver artículos">
                              {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>{c.fecha ? formatDate(c.fecha) : '-'}</TableCell>
                          <TableCell>{formatComprobanteLabel(c)}</TableCell>
                          <TableCell>
                            {c.nroCliente != null
                              ? `${c.nroCliente} - ${c.nombreCliente ?? ''}`
                              : c.nombreCliente ?? '-'}
                          </TableCell>
                          <TableCell>{c.nroSuc ?? '-'}</TableCell>
                          <TableCell>{c.nroUbicacion ?? '-'}</TableCell>
                          <TableCell>{c.venNombreVendedor || '-'}</TableCell>
                          <TableCell>{c.formaDePago || '-'}</TableCell>
                          <TableCell align="right">{formatCurrency(c.importeTotal ?? 0)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={9} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                            <Collapse in={open} timeout="auto" unmountOnExit>
                              <Box sx={{ m: 1.5, p: 1.5, bgcolor: '#fafafa', borderRadius: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                  Artículos
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Ítem</TableCell>
                                      <TableCell>Código</TableCell>
                                      <TableCell>Descripción</TableCell>
                                      <TableCell align="right">Cantidad</TableCell>
                                      <TableCell align="right">P. unitario</TableCell>
                                      <TableCell align="right">Subtotal</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(c.items ?? []).map((it, i) => (
                                      <TableRow key={`${key}-it-${it.nroItem ?? i}`}>
                                        <TableCell>{it.nroItem ?? i + 1}</TableCell>
                                        <TableCell>{it.codigoArticulo || '-'}</TableCell>
                                        <TableCell>{it.descripcion || '-'}</TableCell>
                                        <TableCell align="right">{it.cantidad ?? 0}</TableCell>
                                        <TableCell align="right">
                                          {formatCurrency(it.precioUnitarioFinal ?? it.precioUnitario ?? 0)}
                                        </TableCell>
                                        <TableCell align="right">
                                          {formatCurrency(it.subtotalFinal ?? it.subtotal ?? 0)}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                    {(c.items ?? []).length === 0 && (
                                      <TableRow>
                                        <TableCell colSpan={6} align="center">
                                          Sin ítems
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {rows.length > 0 && (
            <TablePagination
              component="div"
              count={rows.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Filas"
            />
          )}
        </CardContent>
      </Card>

      <ClienteSearch
        open={openClienteSearch}
        onClose={() => setOpenClienteSearch(false)}
        excluirInactivos={false}
        onSelect={(c) => {
          setNroCliente(c.nroCliente);
          setNombreCliente(c.nombre ?? '');
          setOpenClienteSearch(false);
        }}
      />
    </Box>
  );
};

export default InformeVentasResultados;
